import { TextField, Modal, Select as PSelect, Banner, ChoiceList, Link, Toast, Text, FormLayout, Box } from '@shopify/polaris';
import React, { useState, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useAuthStore, useLookupStore } from '../../../context/useStore.js';
import getSharePointUrlForFirnrDocumentTypeIdDocName, { getSharePointPathForFirnrDocumentTypeIdDocName, GENERAL_CONDITIONS_DOC_NAMES, languageMap } from '../../../utils/sharepoint.js';
import { setVerstuurd } from '../../requests/setVerstuurd.js';
import { alphabetize, getRequest } from '../functions.jsx';
import { performTracking } from '../../../analytics/segment';

//FIXME: in the react-selects it is possible to create new options, but these need to be email addresses, needs to be checked if
//this is the case when it is created, we can use a library for this

export default function DocumentCardModal({ resource, resourceId, modalActive, setModalActive, company, docTypeID, docNumber, sysDoc, documents, setResource, docLang, reminderInvoices }) {
  const { user } = useAuthStore();
  const { emailformats, languages, documenttypes, internalUsers, setUsers } = useLookupStore();

  const [mailTo, setMailTo] = useState(null);
  const [mailCcs, setMailCcs] = useState([]);
  const [mailBccs, setMailBccs] = useState([]);
  const [language, setLanguage] = useState(company.TaalID || '3');
  const [contacts, setContacts] = useState([]);
  const [mailSubject, setMailSubject] = useState('');
  const [mailContent, setMailContent] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [sendMailButtonLoading, setSendMailButtonLoading] = useState(false);
  const documentsToAttach = documents.filter((d) => d.DocumentTypeID !== 15);
  // every extra document starts selected, the user only unticks the files that should not go along
  const [selectedDocuments, setSelectedDocuments] = useState(() => documentsToAttach.map((doc) => doc.DocumentID));
  const [companyState, setCompanyState] = useState({});
  const [ccOptions, setCcOptions] = useState([]);
  const selectedExtraCount = documentsToAttach.filter((doc) => selectedDocuments.includes(doc.DocumentID)).length;

  //TOAST STATES
  const [toastOkay, setToastOkay] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [toastErrorMessage, setToastErrorMessage] = useState('Error sending email!');
  const [toastAttachementsTooBig, setToastAttachementsTooBig] = useState(false);
  const [toastDocumentSent, setToastDocumentSent] = useState(false);

  const toastOkayMarkup = toastOkay ? <Toast content="👍 Email is on its way! " onDismiss={() => setToastOkay(false)} /> : null;
  const toastErrorMarkup = toastError ? <Toast error content={toastErrorMessage} onDismiss={() => setToastError(false)} /> : null;
  const toastAttachementsTooBigMarkup = toastAttachementsTooBig ? <Toast error content="💣  Attachments are too big! " onDismiss={() => setToastAttachementsTooBig(false)} /> : null;
  const toastDocumentSentMarkup = toastDocumentSent ? <Toast content="✔️  Document marked as sent! " onDismiss={() => setToastDocumentSent(false)} /> : null;

  const taalIdToKey = { 1: 'NL', 2: 'FR', 3: 'UK', 4: 'DE' };

  useEffect(() => {
    const fetchItems = async () => {
      const data = await getRequest(`/api/contacts?limit=10000&FIRNR=${company.FIRNR}`);
      if (data && Array.isArray(data)) {
        setContacts(data);

        // Create CC options with internal users and company contacts
        const internalUserOptions =
          internalUsers && internalUsers.length > 0
            ? internalUsers
                .filter((usr) => usr.Email && usr.Email !== '')
                .map((usr) => ({
                  value: `internal_${usr.GebruikerID}`,
                  label: `👥 ${usr.Gebruikernaam} (${usr.Email}) - Internal`,
                  email: usr.Email,
                  isInternal: true,
                  userType: usr.Gebruikertype
                }))
            : [];

        const companyContactOptions = data
          .filter((con) => !con.DISCONTINUED && con['E-mail'] !== null)
          .sort((a, b) => alphabetize(a, b, 'Afkorting'))
          .map((con) => ({
            value: con.CONNR,
            label: `${con.NAAM} (${con['E-mail']})`,
            TaalID: con.TaalID,
            email: con['E-mail'],
            isInternal: false
          }));

        const allOptions = [...companyContactOptions, ...internalUserOptions];
        setCcOptions(allOptions);
      }
      setDataLoaded(true);
    };

    if (typeof company.FIRNR !== 'undefined' && company.FIRNR !== null) fetchItems();
    setCompanyState(company);
    //eslint-disable-next-line
  }, [company.FIRNR, internalUsers]);

  // Ensure users are loaded when component mounts
  useEffect(() => {
    if (internalUsers.length === 0) {
      setUsers();
    }
  }, [internalUsers.length, setUsers]);

  // Ensure users are loaded when modal opens
  useEffect(() => {
    if (modalActive && internalUsers.length === 0) {
      setUsers();
    }
  }, [modalActive, internalUsers.length, setUsers]);

  useEffect(() => {
    if (emailformats && emailformats.length > 0) {
      const emailFormat = emailformats.find((em) => em.DocumentTypeID === docTypeID);
      const startKey = 'EmailBodyStart' + taalIdToKey[language];
      const bodyKey = 'EmailBody' + taalIdToKey[language];
      setMailContent(`${emailFormat[startKey]}\r\r${emailFormat[bodyKey]}\r${user?.full_name}`);

      const subjectKey = 'EmailSubject' + taalIdToKey[language];
      setMailSubject(`${emailFormat[subjectKey]} ${docNumber}`);
    }
    //eslint-disable-next-line
  }, [language, docNumber, companyState, emailformats]);

  useEffect(() => {
    if (dataLoaded && contacts.map((con) => con['E-mail']).includes(mailTo.email)) setLanguage(mailTo.TaalID);
    //eslint-disable-next-line
  }, [mailTo]);

  const getDocumentSharePointPath = (document) => {
    return getSharePointPathForFirnrDocumentTypeIdDocName(
      document.klantnummer || company.Klantnummer,
      document.DocumentTypeID || docTypeID,
      document.documentNumber || document.docNumber || docNumber,
      false,
      documenttypes
    );
  };

  const getDocumentSharePointUrl = (document) => {
    return getSharePointUrlForFirnrDocumentTypeIdDocName(
      document.klantnummer || company.Klantnummer,
      document.DocumentTypeID || docTypeID,
      document.documentNumber || document.docNumber || docNumber,
      document.Filename,
      documenttypes
    );
  };

  const getEmailErrorMessage = (data) => {
    const document = data?.document;

    if (data?.error === 'sharepoint_document_not_found' && document?.filename) {
      return `Document not found in SharePoint: ${document.filename}${document.path ? ` at ${document.path}` : ''}. Re-upload it or deselect it and try again.`;
    }

    if (data?.error === 'sharepoint_document_download_failed' && document?.filename) {
      return `Could not download SharePoint document: ${document.filename}${document.path ? ` at ${document.path}` : ''}.`;
    }

    return data?.message || 'Error sending email!';
  };

  const handlePrepareEmail = async () => {
    setSendMailButtonLoading(true);
    const sharepointUploadPath = getSharePointPathForFirnrDocumentTypeIdDocName(company.Klantnummer, docTypeID, docNumber, false, documenttypes);
    /*
      Sharepoint docs is an array of 3 categories of documents that will be send as attachements in the email
      1: The systen document (i.e. invoice, sales order etc..)
      2: Link to general conditions file (in function of the language)
      3: All the other uploaded files, the user can choose which ones to send in this modal
    */

    let sharepointDocs = [{ sharepointDocName: sysDoc.Filename, sharepointPath: sharepointUploadPath, documentId: sysDoc.DocumentID }];

    if (docTypeID === 15) sharepointDocs = sharepointDocs.concat(reminderInvoices);

    sharepointDocs = sharepointDocs.concat([
      ...documents
        .filter((document) => selectedDocuments.includes(document.DocumentID)) //only the ones that are selected by the user
        .map((document) => ({
          sharepointDocName: document.Filename,
          sharepointPath: getDocumentSharePointPath(document),
          documentId: document.DocumentID,
          documentTypeId: document.DocumentTypeID,
          documentNumber: document.documentNumber || document.docNumber,
          customerNumber: document.klantnummer || company.Klantnummer
        }))
    ]);

    if (docTypeID !== 13) sharepointDocs.push({ sharepointDocName: GENERAL_CONDITIONS_DOC_NAMES[languageMap[docLang]], sharepointPath: null });

    const payload = {
      emailSubject: mailSubject,
      emailBody: mailContent,
      replyTo: user.email,
      toAddress: mailTo.email,
      cc: [user.email, ...mailCcs.map((cc) => cc.email)],
      bcc: mailBccs.map((bcc) => bcc.email),
      docTypeID: docTypeID,
      docNumber: docNumber,
      sharepointDocs,
      resource,
      resourceId,
      docLang: languageMap[language]
    };

    const response = await fetch('/api/email/systemdoc', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    // json response is needed either way for the toasts:
    const data = await response.json();

    if (data.error) {
      const { error } = data;
      if (error === 'attachements_too_big') {
        setToastAttachementsTooBig(true);
        setSendMailButtonLoading(false);
      } else {
        setToastErrorMessage(getEmailErrorMessage(data));
        setToastError(true);
        setSendMailButtonLoading(false);
      }
    } else {
      setToastOkay(true);
      const pathNameArray = window.location.pathname.split('/');
      const resourceName = docTypeID === 15 ? 'payment_reminders/mark_as_sent' : pathNameArray[1];
      const resourceID = docTypeID === 15 ? resourceId : pathNameArray[2];
      const resource = await setVerstuurd(resourceName, resourceID, docTypeID);

      //analytics
      performTracking('email_systemdoc', payload);

      if (resource) setResource(resource);
      setToastDocumentSent(true);
      setSendMailButtonLoading(false);
      setModalActive(false);
    }
  };

  return (
    <Modal
      size="large"
      open={modalActive}
      onClose={() => setModalActive(false)}
      title="Email configuration"
      primaryAction={{
        content: selectedExtraCount > 0 ? `SEND EMAIL (+${selectedExtraCount} extra ${selectedExtraCount === 1 ? 'file' : 'files'})` : 'SEND EMAIL',
        disabled: !mailTo,
        loading: sendMailButtonLoading,
        onAction: handlePrepareEmail
      }}
      secondaryActions={{
        content: 'Cancel',
        onAction: () => {
          setModalActive(false);
          setSendMailButtonLoading(false);
        }
      }}
    >
      {toastOkayMarkup}
      {toastErrorMarkup}
      {toastAttachementsTooBigMarkup}
      {toastDocumentSentMarkup}
      <Modal.Section>
        <Box paddingBlockEnd="400">
          <Banner
            tone="info"
            title="The email will be automatically sent to you in CC, you do not need to add yourself as CC. The system document will be automatically send as attachment. Internal team members are available in the CC dropdown for easy selection."
          />
        </Box>

        <FormLayout>
          <Box>
            <div style={{ marginBottom: '4px' }}>
              <Text>Send email to</Text>
            </div>
            <CreatableSelect
              isClearable
              onChange={(props, actionMeta) => {
                if (actionMeta.action === 'create-option') return setMailTo({ ...props, email: props.value });
                setMailTo({ ...props });
              }}
              options={contacts
                ?.filter((con) => !con.DISCONTINUED && con['E-mail'] !== null)
                .sort((a, b) => alphabetize(a, b, 'NAAM'))
                .map((con) => ({ value: con.CONNR, label: `${con.NAAM} (${con['E-mail']})`, TaalID: con.TaalID, email: con['E-mail'] }))}
              menuPortalTarget={document.body}
              styles={{
                valueContainer: (base) => ({ ...base, height: '32px' }),
                indicatorSeperator: (base) => ({ ...base, height: '32px' }),
                indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                control: (base) => ({ ...base, height: '32px', minHeight: '32px', borderRadius: '8px', borderColor: '#919191' }),
                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
          </Box>

          <FormLayout.Group>
            <Box>
              <div style={{ marginBottom: '4px' }}>
                <Text>CC</Text>
              </div>
              <CreatableSelect
                options={ccOptions}
                onChange={(props) => setMailCcs([...props])}
                isMulti
                onCreateOption={(value) => setMailCcs([...mailCcs, { label: value, email: value }])}
                value={mailCcs}
                menuPortalTarget={document.body}
                styles={{
                  valueContainer: (base) => ({ ...base, height: '32px' }),
                  indicatorSeperator: (base) => ({ ...base, height: '32px' }),
                  indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                  control: (base) => ({ ...base, height: '32px', minHeight: '32px', borderRadius: '8px', borderColor: '#919191' }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </Box>

            <Box>
              <div style={{ marginBottom: '4px' }}>
                <Text>BCC</Text>
              </div>
              <CreatableSelect
                options={ccOptions}
                onChange={(props) => setMailBccs([...props])}
                isMulti
                onCreateOption={(value) => setMailBccs([...mailBccs, { label: value, email: value }])}
                value={mailBccs}
                menuPortalTarget={document.body}
                styles={{
                  valueContainer: (base) => ({ ...base, height: '32px' }),
                  indicatorSeperator: (base) => ({ ...base, height: '32px' }),
                  indicatorsContainer: (base) => ({ ...base, height: '32px' }),
                  control: (base) => ({ ...base, height: '32px', minHeight: '32px', borderRadius: '8px', borderColor: '#919191' }),
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                }}
              />
            </Box>
          </FormLayout.Group>

          <PSelect
            label="Language"
            options={languages?.sort((a, b) => alphabetize(a, b, 'Omschrijving')).map((lan) => ({ value: lan.TaalID, label: lan.Omschrijving }))}
            value={parseInt(language)}
            onChange={(value) => setLanguage(value)}
          />
          <TextField label="Mail Subject" value={mailSubject} onChange={(props) => setMailSubject(props)} />
          <TextField label="Mail content" value={mailContent} onChange={(val) => setMailContent(val)} multiline={10} autoComplete="off" />

          {documentsToAttach.length > 0 && (
            <>
              <Box paddingBlockEnd="400" paddingBlockStart="400">
                <Banner
                  tone="warning"
                  title={
                    selectedExtraCount > 0
                      ? `${selectedExtraCount} of ${documentsToAttach.length} extra uploaded ${documentsToAttach.length === 1 ? 'document' : 'documents'} will be attached to this email`
                      : `No extra uploaded documents will be attached (0 of ${documentsToAttach.length} selected)`
                  }
                >
                  All extra documents are selected by default. Untick the ones that should not be sent (total max of 7MB).
                </Banner>
              </Box>
              <Box paddingBlockEnd="200">
                <button
                  type="button"
                  onClick={() => {
                    if (selectedDocuments.length === documentsToAttach.length) {
                      setSelectedDocuments([]);
                    } else {
                      setSelectedDocuments(documentsToAttach.map((doc) => doc.DocumentID));
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f6f6f7',
                    color: '#202223',
                    border: '1px solid #c9cccf',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e1e3e5';
                    e.target.style.borderColor = '#8c9196';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f6f6f7';
                    e.target.style.borderColor = '#c9cccf';
                  }}
                >
                  {selectedDocuments.length === documentsToAttach.length ? 'Deselect All' : 'Select All'}
                </button>
              </Box>
              <ChoiceList
                allowMultiple
                choices={documentsToAttach.map((doc, idx) => ({
                  key: idx,
                  label: doc.Filename,
                  value: doc.DocumentID,
                  helpText: (
                    <Link key={idx} url={getDocumentSharePointUrl(doc)}>
                      open file
                    </Link>
                  )
                }))}
                selected={selectedDocuments}
                onChange={setSelectedDocuments}
              />
            </>
          )}
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
}
